<template>
  <!-- Upgrade a trial to a paid subscription now -->
  <Modal
    v-model="showUpgrade"
    :title="t('licensing.trial.upgradeTitle')"
    size="wide"
  >
    <div class="flex flex-col gap-4">
      <p class="text-sm text-content dark:text-content-dark">
        {{ t("licensing.trial.upgradeExplanation") }}
        <!-- no Stripe subscription yet: paid on Checkout -->
        <template v-if="viaCheckout">
          {{ t("licensing.trial.upgradeCheckoutHint") }}
        </template>
      </p>

      <Input
        v-model="seatsRaw"
        type="number"
        step="1"
        :min="minSeats"
        :label="t('licensing.trial.upgradeSeatsLabel')"
        :error="seatsError"
      />
      <p class="text-xs text-muted-text dark:text-muted-text-dark">
        {{ t("licensing.trial.upgradeSeatsMin", { seats: minSeats }) }}
      </p>

      <!-- Checkout: both choices, as at the card step. Upgrade: the term can
           change, the payment option is the one chosen at the card step. -->
      <ContractChoice
        v-model:term="contractTerm"
        v-model:option="paymentOption"
        :org-id="orgId"
        :seats="seats ?? minSeats"
        :option-fixed="!viaCheckout"
      />

      <p
        v-if="price && seats !== null && paymentOption"
        class="text-sm text-heading dark:text-heading-dark"
      >
        {{
          t(
            taxIncluded
              ? "licensing.trial.upgradeTotalTaxIncluded"
              : "licensing.trial.upgradeTotal",
            {
              total: periodTotal(paymentOption, seats),
              interval: intervalLabel(paymentOption),
            },
          )
        }}
      </p>

      <div v-if="serverError" class="flex flex-col items-start gap-2">
        <Alert :message="serverError" variant="error" />
        <!-- declined card or no card: fixed in the billing portal -->
        <Button
          v-if="paymentProblem"
          variant="secondary"
          type="button"
          :disabled="billingPortal.opening.value"
          @click="billingPortal.open"
        >
          <MdiLoading
            v-if="billingPortal.opening.value"
            class="animate-spin mr-2"
          />
          {{ t("licensing.billing.updatePaymentMethod") }}
        </Button>
        <span
          v-if="billingPortal.errorKey.value"
          class="text-sm text-danger-text dark:text-danger-text-dark"
        >
          {{ t(billingPortal.errorKey.value) }}
        </span>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-wrap justify-end gap-3 p-4 sm:p-6 pt-4">
        <Button variant="secondary" type="button" @click="showUpgrade = false">
          {{ t("components.modal.cancel") }}
        </Button>
        <Button
          variant="primary"
          type="button"
          :disabled="seats === null || !!seatsError || !contract || submitting"
          @click="submitUpgrade"
        >
          <MdiLoading v-if="submitting" class="animate-spin mr-2" />
          {{
            viaCheckout
              ? t("licensing.trial.upgradeContinueToPayment")
              : t("licensing.trial.upgradeConfirm")
          }}
        </Button>
      </div>
    </template>
  </Modal>

  <!-- Cancel the trial -->
  <Modal
    v-model="showCancel"
    :title="t('licensing.trial.cancelTitle')"
    size="medium"
  >
    <div class="flex flex-col gap-4">
      <p class="text-sm text-content dark:text-content-dark">
        {{ t("licensing.trial.cancelMessage", { date: endsOnText }) }}
      </p>
      <Alert v-if="serverError" :message="serverError" variant="error" />
    </div>

    <template #footer>
      <div class="flex flex-wrap justify-end gap-3 p-4 sm:p-6 pt-4">
        <Button variant="secondary" type="button" @click="showCancel = false">
          {{ t("licensing.trial.cancelKeep") }}
        </Button>
        <Button
          variant="danger"
          type="button"
          :disabled="submitting"
          @click="submitCancel"
        >
          <MdiLoading v-if="submitting" class="animate-spin mr-2" />
          {{ t("licensing.trial.cancelConfirm") }}
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, toRef } from "vue";
import { useI18n } from "vue-i18n";
import { ApiErrorCode, isApiResponseError, Org } from "tf-api-client";
import api from "../../api";
import { useBillingPortal } from "../../composables/use-billing-portal";
import { useSeatPrice } from "../../composables/use-seat-price";
import { isSelfServiceBilling, SUBSCRIPTION_ROUTE } from "../../config/licensing";
import i18nUtil from "../../utils/i18n";
import { apiErrorText } from "../../utils/api-error";
import Modal from "../Base/Modal.vue";
import Input from "../Base/Input.vue";
import Button from "../Base/Button.vue";
import Alert from "../Base/Alert.vue";
import ContractChoice from "./ContractChoice.vue";
import MdiLoading from "~icons/mdi/loading";

const props = defineProps<{
  orgId: string | null;
  subscription: Org.Subscription | null;
}>();

const emit = defineEmits<{
  // the upgrade or cancellation succeeded: the new subscription
  upgraded: [subscription: Org.Subscription];
  cancelled: [subscription: Org.Subscription];
  // the server showed the local view was stale (e.g. no longer a trial)
  stale: [];
}>();

const { t, te } = useI18n();
// the seat price exists only with self-service billing (404 otherwise); the
// upgrade dialog is never offered without it
const { price, intervalLabel, periodTotal, taxIncluded } = useSeatPrice(() =>
  isSelfServiceBilling(props.subscription) ? props.orgId : null,
);
const billingPortal = useBillingPortal(toRef(props, "orgId"));

const showUpgrade = ref(false);
const showCancel = ref(false);
const submitting = ref(false);
const serverError = ref("");
// 402 declined / 409 no card: offer the billing portal
const paymentProblem = ref(false);

// A self-service trial without a Stripe subscription (e.g. cancelled here)
// is bought on the paid Checkout, which comes back to the subscription page.
const viaCheckout = computed(() => props.subscription?.billingLinked === false);

// The backend never goes below the seats in use (at least 1).
const minSeats = computed(() =>
  Math.max(1, props.subscription?.assignedSeats ?? 1),
);

// The contract the subscription starts with. Checkout: chosen here, nothing
// preselected. Upgrade: the trial's, whose term can change.
const contractTerm = ref<Org.ContractTermMonths | null>(null);
const paymentOption = ref<Org.SubscriptionBillingInterval | null>(null);
const contract = computed<Org.ContractChoice | null>(() =>
  contractTerm.value && paymentOption.value
    ? {
        contractTermMonths: contractTerm.value,
        billingInterval: paymentOption.value,
      }
    : null,
);

const seatsRaw = ref("");
const seats = computed(() => {
  const n = Number(seatsRaw.value);
  return seatsRaw.value !== "" && Number.isInteger(n) ? n : null;
});
const seatsError = computed(() =>
  seats.value !== null && seats.value < minSeats.value
    ? t("licensing.trial.upgradeSeatsTooFew", { seats: minSeats.value })
    : null,
);

const endsOnText = computed(() => {
  const sub = props.subscription;
  return sub ? i18nUtil.formatLongDate(sub.trialEndsAt ?? sub.termEndDate) : "";
});

const errorText = (err: unknown): string => {
  if (!isApiResponseError(err)) return t("errors.unknown_error");
  if (err.is(ApiErrorCode.SubscriptionLimitExceeded)) {
    return t("licensing.trial.upgradeSeatsTooFew", { seats: minSeats.value });
  }
  if (err.is(ApiErrorCode.InvalidResource)) {
    return t("licensing.contract.invalid");
  }
  if (err.is(ApiErrorCode.InvalidResourceAction)) {
    return t("licensing.contract.paymentOptionFixed");
  }
  return apiErrorText({ t, te }, err);
};

const reset = () => {
  serverError.value = "";
  paymentProblem.value = false;
  submitting.value = false;
};

const openUpgrade = () => {
  reset();
  seatsRaw.value = String(minSeats.value);
  const sub = props.subscription;
  if (viaCheckout.value) {
    contractTerm.value = null;
    paymentOption.value = null;
  } else {
    contractTerm.value = sub?.contractTermMonths ?? null;
    paymentOption.value = sub?.billingInterval ?? null;
  }
  showUpgrade.value = true;
};

const openCancel = () => {
  reset();
  showCancel.value = true;
};

// The contract is only sent when it differs from the trial's (both or
// neither): the default is the trial's term and payment option.
const upgrade = async (
  orgId: string,
  chosen: number | undefined,
  choice: Org.ContractChoice,
) => {
  const sub = props.subscription;
  const changed =
    choice.contractTermMonths !== sub?.contractTermMonths ||
    choice.billingInterval !== sub?.billingInterval;
  const subscription = await api.org.upgradeSubscription(
    orgId,
    chosen,
    changed ? choice : undefined,
  );
  showUpgrade.value = false;
  emit("upgraded", subscription);
};

// Leaves for Checkout in the same tab; the button stays disabled until then.
let leaving = false;
const checkout = async (
  orgId: string,
  chosen: number | undefined,
  choice: Org.ContractChoice,
) => {
  const returnUrl = new URL(SUBSCRIPTION_ROUTE, window.location.origin).href;
  const { url } = await api.org.createSubscriptionCheckout(
    orgId,
    returnUrl,
    choice,
    chosen,
  );
  leaving = true;
  window.location.assign(url);
};

// Back from Checkout with the browser's back button: the page may be
// restored from the back-forward cache with the button still disabled.
const onPageShow = (event: PageTransitionEvent) => {
  if (event.persisted && leaving) {
    leaving = false;
    submitting.value = false;
  }
};
onMounted(() => window.addEventListener("pageshow", onPageShow));
onUnmounted(() => window.removeEventListener("pageshow", onPageShow));

const is = (err: unknown, code: ApiErrorCode) =>
  isApiResponseError(err) && err.is(code);

const submitUpgrade = async () => {
  const orgId = props.orgId;
  const choice = contract.value;
  if (!orgId || seats.value === null || !choice || submitting.value) return;
  submitting.value = true;
  serverError.value = "";
  paymentProblem.value = false;

  try {
    // the default (no seats) is the seats in use: only send a choice
    const chosen = seats.value === minSeats.value ? undefined : seats.value;
    // Each path falls back to the other when the server says the local
    // `billingLinked` was stale.
    if (viaCheckout.value) {
      try {
        await checkout(orgId, chosen, choice);
      } catch (err) {
        if (!is(err, ApiErrorCode.BillingAlreadyLinked)) throw err;
        await upgrade(orgId, chosen, choice);
      }
    } else {
      try {
        await upgrade(orgId, chosen, choice);
      } catch (err) {
        if (!is(err, ApiErrorCode.BillingCheckoutRequired)) throw err;
        await checkout(orgId, chosen, choice);
      }
    }
  } catch (err) {
    serverError.value = errorText(err);
    paymentProblem.value =
      isApiResponseError(err) &&
      (err.is(ApiErrorCode.BillingPaymentDeclined) ||
        err.is(ApiErrorCode.BillingPaymentMethodMissing));
    // no longer a trial, or already billed by Stripe with another payment
    // option: the local view was stale
    if (
      is(err, ApiErrorCode.SubscriptionNotTrial) ||
      is(err, ApiErrorCode.InvalidResourceAction)
    ) {
      emit("stale");
    }
  } finally {
    if (!leaving) submitting.value = false;
  }
};

const submitCancel = async () => {
  if (!props.orgId || submitting.value) return;
  submitting.value = true;
  serverError.value = "";

  try {
    const subscription = await api.org.cancelTrial(props.orgId);
    showCancel.value = false;
    emit("cancelled", subscription);
  } catch (err) {
    serverError.value = errorText(err);
    // already cancelled or converted meanwhile
    if (
      is(err, ApiErrorCode.SubscriptionNotTrial) ||
      is(err, ApiErrorCode.TrialNotRunning)
    ) {
      emit("stale");
    }
  } finally {
    submitting.value = false;
  }
};

defineExpose({ openUpgrade, openCancel });
</script>
